import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React from 'react';
import {ContextualMenuControlsOption} from '@Components/contextual-menu/components/contextual-menu-controls-option';
import {useGetNumberOfActiveItems} from '@Hooks/poster-editor/useGetNumberOfActiveItems';
import {useAppSelector} from '@/hooks';

enum GroupItemDefaultControlOptions {
  BACKWARD = 'backward',
  FORWARD = 'forward',
  DUPLICATE = 'duplicate',
  DELETE = 'delete',
  CLEAR_SELECTION = 'clear_selection',
}

export function GroupItemsDefaultOptions(): ReactElement<any> {
  const numberOfActiveItems = useGetNumberOfActiveItems();
  const itemsOrder = useAppSelector((state) => {
    return state.posterEditor.posterObject?.pages.pagesHashMap[state.posterEditor.currentPageId]?.items.itemsOrder;
  });
  const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
  const canBeMovedBackwards = useMemo(() => {
    return currentPage?.items.canSelectedItemsBeMovedBackwards();
  }, [itemsOrder]);
  const canBeMovedForward = useMemo(() => {
    return currentPage?.items.canSelectedItemsBeMovedForward();
  }, [itemsOrder]);

  if (!currentPage) {
    throw new Error('Could not get the current page');
  }

  if (numberOfActiveItems === 0) {
    throw new Error('Active items donot exist - unable to show group items default options');
  }

  const onClearSelection = (): void => {
    currentPage.activeSelection?.clearSelection();
  };

  const onDelete = (): void => {
    currentPage.activeSelection?.deleteSelectedItems();
  };

  const onDuplicate = (): void => {
    currentPage.activeSelection?.onDuplicate();
  };

  const onClickHandler = (clickedId: GroupItemDefaultControlOptions): void => {
    switch (clickedId) {
      case GroupItemDefaultControlOptions.CLEAR_SELECTION:
        onClearSelection();
        return;
      case GroupItemDefaultControlOptions.DELETE:
        onDelete();
        return;
      case GroupItemDefaultControlOptions.DUPLICATE:
        onDuplicate();
        return;
      case GroupItemDefaultControlOptions.FORWARD:
        currentPage?.items.bringForwardSelectedItem();
        return;
      case GroupItemDefaultControlOptions.BACKWARD:
        currentPage?.items.sendBackwardsSelectedItem();
        return;
      default:
        throw new Error('unhandled group item default option');
    }
  };

  return (
    <ContextualMenuControlsOption
      id="group-items-default-option"
      isCenterAligned
      onClick={onClickHandler}
      data={[
        {
          id: GroupItemDefaultControlOptions.BACKWARD,
          icon: 'icon-send-to-back',
          text: window.i18next.t('pmwjs_backward'),
          disabled: !canBeMovedBackwards,
        },
        {
          id: GroupItemDefaultControlOptions.FORWARD,
          icon: 'icon-send-to-front',
          text: window.i18next.t('pmwjs_forward'),
          disabled: !canBeMovedForward,
        },
        {
          id: GroupItemDefaultControlOptions.DUPLICATE,
          icon: 'icon-duplicate',
          text: window.i18next.t('pmwjs_duplicate'),
        },
        {
          id: GroupItemDefaultControlOptions.DELETE,
          icon: 'icon-delete',
          iconClasses: 'color-danger-100',
          text: window.i18next.t('pmwjs_delete'),
        },
      ]}
    />
  );
}
